import type { Board } from "./board";
import { PRIORITY_FIRST, isProjectileHittable, type MultiPassProcessableThing, type OnAddToBoard } from "./interfaces";
import { PhysicalThing } from "./physicalThing";
import { PhysicalThingWithHp } from "./physicalThingWithHp";
import type { Thing } from "./thing";
import type { XY } from "./xy";
import { ZOrder } from "./zOrder";

export abstract class Projectile extends PhysicalThing implements MultiPassProcessableThing, OnAddToBoard {
	private hitThisPass: Set<Thing> = new Set();

	constructor(xy: XY) {
		super(xy, false);
	}

	getZOrder(): number {
		return ZOrder.PROJECTILE;
	}

	getPassesToRun(): number[] {
		return [0, 1];
	}

	getProcessPriority(): number {
		return PRIORITY_FIRST + 1;
	}

	processTurn(board: Board): void {
		if (board.currentProcessingPass === 0) {
			this.hitThisPass.clear();
		}
	}


	protected moveTo(destination: XY): boolean {
		if (!this.board!.isInBounds(destination)) {
			this.board!.removeAtEndOfTurn(this);
			return false;
		}
		this.xy = destination;
		this.checkForTarget();
		if (this.active) {
			this.board!.runLater(() => {
				if (this.active) this.checkForTarget();
			});
		}
		return true;
	}

	checkForTarget(): boolean {
		if (!this.active) return false;
		const targets = this.board!.getCell(this.xy)
			.getThings()
			.filter((t) => t.active)
			.filter((t) => isProjectileHittable(t) && t.isHittable())
			.filter((t) => !this.hitThisPass.has(t));

		let totalDamageDealt = 0;
		for (const target of targets) {
			if (!target.active) continue;
			this.hitThisPass.add(target);
			(target as unknown as { onProjectileHit(p: Projectile): void }).onProjectileHit(this);

			if (target instanceof PhysicalThingWithHp) {
				totalDamageDealt += this.damageTarget(target);
			}
		}

		if (targets.length > 0) {
			this.onHit(totalDamageDealt);
		}

		return targets.length > 0;
	}

	protected abstract damageTarget(target: PhysicalThingWithHp): number;
	public abstract onHit(damageDealt: number): void;

	onAddToBoard(): void {
		this.checkForTarget();
		this.board!.runLater(() => {
			if (this.active) this.checkForTarget();
		});
	}
}
